import { mixpanelObject } from 'src/utils/analytics';
import {
  addToWatchlistEvent,
  contentPropertySetter,
  removeFromWatchlistEvent
} from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import { aggregatorPropertySetter } from 'src/utils/analytics/mixpanel/propertySetter';
import {
  ContentItemType,
  DynamicObject,
  UserWatchlistType
} from 'src/utils/commonTypes';
import { MATCH_CLIPS, SPORTS_ROUTE } from 'src/utils/constants/ApiConstants';
import {
  ASSET_SUBTYPE,
  ASSET_TYPE,
  BUSINESS_TYPE
} from 'src/utils/constants/AppConstants';
import { Episode } from '../../../services/GraphQL/Queries/types';
import { watchlistItem } from '../../../services/responseTypes/watchlistResponse';

import {
  MIXPANEL_ELEMENT,
  MIXPANEL_WATCHLIST
} from '../../../utils/analytics/mixpanel/mixpanelConstants';

export const isTvod = (type: string) => {
  if (type === BUSINESS_TYPE.TVOD || type === BUSINESS_TYPE.TVOD_DOWNLOADABLE) {
    return true;
  }
  return false;
};

export const isSportsVOD = ({
  assetSubType = '',
  assetType = NaN,
  path = ''
}) => {
  return (
    (Number(assetType) === ASSET_TYPE.Episode &&
      assetSubType === ASSET_SUBTYPE.SPORTS_VOD) ||
    path === `/${SPORTS_ROUTE}/${MATCH_CLIPS}/:showId/:episodeName/:episodeid`
  );
};

const convertWatchlistDataToArray = (watchlistData: { [key: string]: any }) => {
  const watchlistItems = [];
  for (const contentType in watchlistData) {
    watchlistItems.push(...watchlistData[contentType]);
  }
  return watchlistItems;
};

export const prepareAppStoreWatchlistObject = (watchlistData: {
  [key: string]: watchlistItem;
}): UserWatchlistType => {
  const userWatchlist: UserWatchlistType = {
    episodes: [],
    movies: [],
    videos: [],
    sports: [],
    plex: [],
    isWatchlistDataFetched: false
  };
  const watchlistItems = convertWatchlistDataToArray(watchlistData);

  watchlistItems.map((item) => {
    if (item.asset_type === 0) {
      if (
        item.assetSub_type === ASSET_SUBTYPE.MOVIE &&
        isTvod(item.businessType)
      ) {
        userWatchlist.plex.push(item);
      } else if (item.assetSub_type === ASSET_SUBTYPE.MOVIE) {
        userWatchlist.movies.push(item);
      } else {
        userWatchlist.videos.push(item);
      }
    } else if (item.asset_type >= 1) {
      if (
        isSportsVOD({
          assetSubType: item?.assetSub_type,
          assetType: item?.asset_type
        })
      ) {
        userWatchlist.sports.push(item);
      } else {
        userWatchlist.episodes.push(...item.episodes);
      }
    }
  });
  return userWatchlist;
};

export const checkIfContentAvailableInWatchlist = (
  UserWatchlist: UserWatchlistType,
  item: ContentItemType | Episode
) => {
  let watchlistContentList;
  if (UserWatchlist) {
    watchlistContentList = [
      ...UserWatchlist.episodes,
      ...UserWatchlist.movies,
      ...UserWatchlist.plex,
      ...UserWatchlist.sports,
      ...UserWatchlist.videos
    ];
  }

  const isItemPresentInWatchlist =
    watchlistContentList &&
    watchlistContentList.filter((content) => content.id === item.id).length > 0;

  return isItemPresentInWatchlist;
};

export const addWatchlistItem = (
  userWatchlist: UserWatchlistType,
  item: ContentItemType
) => {
  const data = userWatchlist;
  if (Number(item.assetType) === 0) {
    if (item.assetSubType === 'movie' && isTvod(item.businessType)) {
      data.plex.push(item);
    } else if (item.assetSubType === 'movie') {
      data.movies.push(item);
    } else {
      data.videos.push(item);
    }
  } else if (Number(item.assetType) === 1) {
    if (
      isSportsVOD({
        assetSubType: item?.assetSubType,
        assetType: item?.assetType
      })
    ) {
      data.sports.push(item);
    } else {
      data.episodes.push(item);
    }
  }
  return data;
};

export const removeWatchlistItem = (
  userWatchlist: UserWatchlistType,
  item: ContentItemType
) => {
  const data = userWatchlist;
  if (Number(item.assetType) === 0) {
    if (isTvod(item.businessType) && item.assetSubType === 'movie') {
      data.plex.forEach((element: ContentItemType, i: number) => {
        if (element.id === item.id) {
          data.plex.splice(i, 1);
        }
      });
    } else if (item.assetSubType === 'movie' && Array.isArray(data.movies)) {
      data.movies.forEach((element: ContentItemType, i: number) => {
        if (element.id === item.id) {
          data.movies.splice(i, 1);
        }
      });
    } else if (Array.isArray(data.videos)) {
      data.videos.forEach((element: ContentItemType, i: number) => {
        if (element.id === item.id) {
          data.videos.splice(i, 1);
        }
      });
    }
  } else if (
    (Number(item.assetType) === 1 && Array.isArray(data.episodes)) ||
    Array.isArray(data.sports)
  ) {
    const sportsVod = isSportsVOD({
      assetSubType: item?.assetSubType,
      assetType: item?.assetType
    });
    if (sportsVod) {
      data.sports.forEach((element: ContentItemType, i: number) => {
        if (element.id === item.id) {
          data.sports.splice(i, 1);
        }
      });
    } else {
      data.episodes.forEach((element: ContentItemType, i: number) => {
        if (element.id === item.id) {
          data.episodes.splice(i, 1);
        }
      });
    }
  }
  return data;
};

export const removeFromWatchlistMixpanelEvent = (
  item: ContentItemType,
  contentPartnerDetails?: DynamicObject
) => {
  mixpanelObject.setProperty(MIXPANEL_ELEMENT, MIXPANEL_WATCHLIST);
  contentPropertySetter(mixpanelObject, item, null);
  if (contentPartnerDetails) {
    aggregatorPropertySetter(contentPartnerDetails);
  }
  removeFromWatchlistEvent();
};

export const addToWatchlistMixpanelEvent = (
  item: ContentItemType,
  contentPartnerDetails?: DynamicObject
) => {
  mixpanelObject.setProperty(MIXPANEL_ELEMENT, MIXPANEL_WATCHLIST);
  contentPropertySetter(mixpanelObject, item, null);
  if (contentPartnerDetails) {
    aggregatorPropertySetter(contentPartnerDetails);
  }
  addToWatchlistEvent();
};
